import React, { useState, useMemo, useCallback } from "react";
import { fetchWrapper } from "utils/apiFetch";
import { useSession } from "hooks/useSession";
import { showMainToast } from "utils/showToast";

// ─── constants ────────────────────────────────────────────────────────────────
const SVC_ORDER = [
  "recharge",
  "dth",
  "bbps",
  "aeps",
  "dmt",
  "matm",
  "cms",
  "pan_card",
  "insurance",
  "fastag",
  "lic",
  "wallet_settlement",
  "upi_qr"
];
const SVC_META = {
  recharge: { label: "Mobile Recharge", icon: "ph-device-mobile" },
  dth: { label: "DTH Recharge", icon: "ph-television-simple" },
  bbps: { label: "Bill Payment (BBPS)", icon: "ph-file-text" },
  aeps: { label: "AEPS", icon: "ph-fingerprint" },
  dmt: { label: "DMT Transfer", icon: "ph-paper-plane-right" },
  matm: { label: "MATM", icon: "ph-credit-card" },
  cms: { label: "CMS", icon: "ph-money" },
  pan_card: { label: "PAN Card", icon: "ph-identification-card" },
  insurance: { label: "Insurance", icon: "ph-shield-check" },
  fastag: { label: "FasTag", icon: "ph-car" },
  lic: { label: "LIC Premium", icon: "ph-heart" },
  wallet_settlement: { label: "Wallet Settlement", icon: "ph-wallet" },
  upi_qr: { label: "UPI QR", icon: "ph-qr-code" }
};

const inr = (n) =>
  "₹ " +
  (parseFloat(n) || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

const getTodayRange = () => {
  const t = new Date().toISOString().split("T")[0];
  return { start: t, end: t };
};

const buildUrl = (userid, start, end, targetUser = "") => {
  let url = `https://pro.mokshiq.in/user_wise_report?user=${userid}&filter_date=${start}&end_date=${end}`;
  if (targetUser) url += `&target_user=${targetUser}`;
  return url;
};

// ─── Modern Stat Card ────────────────────────────────────────────────────────────────
function StatCard({ label, value, icon, iconBg, iconColor }) {
  return (
    <div
      className="card border-0 rounded-4 shadow-sm bg-white position-relative overflow-hidden h-100"
      style={{ padding: "10px 14px" }}
    >
      <div className="d-flex align-items-center position-relative z-1" style={{ gap: "14px" }}>
        <div
          className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
          style={{
            width: "36px",
            height: "36px",
            backgroundColor: iconBg,
            color: iconColor
          }}
        >
          <i className={`ph-duotone ${icon}`} style={{ fontSize: "18px" }}></i>
        </div>
        <div className="overflow-hidden min-w-0 flex-grow-1">
          <span
            className="text-muted text-uppercase fw-semibold d-block text-truncate"
            style={{ fontSize: "10.5px", letterSpacing: "0.03em", marginBottom: "1px" }}
            title={label}
          >
            {label}
          </span>
          <h5
            className="fw-extrabold text-dark mb-0 text-truncate"
            style={{ fontSize: "15px", lineHeight: "1.2" }}
          >
            {value}
          </h5>
        </div>
      </div>

      <div
        className="position-absolute end-0 bottom-0 pe-none"
        style={{
          color: iconColor,
          opacity: 0.1,
          right: "6px",
          bottom: "4px"
        }}
      >
        <i className="ph-duotone ph-chart-bar" style={{ fontSize: "32px" }}></i>
      </div>
    </div>
  );
}

// ─── operator mini list ───────────────────────────────────────────────────────
function OperatorList({ operators }) {
  if (!operators || !Object.keys(operators).length)
    return <span className="text-secondary f-s-11">—</span>;
  return (
    <div className="mt-1">
      {Object.entries(operators).map(([op, v], i) => (
        <div
          key={i}
          className="d-flex justify-content-between gap-3 py-1"
          style={{
            borderBottom:
              i < Object.keys(operators).length - 1
                ? "1px dashed #e5e7eb"
                : "none"
          }}
        >
          <span className="f-s-11 text-secondary">
            <i className="ph ph-dot-outline me-1"></i>
            {op}
          </span>
          <span className="f-s-11 f-w-500 text-primary">
            {v.txn} txn · {inr(v.amount)}
          </span>
        </div>
      ))}
    </div>
  );
}

// ─── daily balance table ──────────────────────────────────────────────────────
function DailyBalanceTable({ rows }) {
  if (!rows || !rows.length) return null;
  return (
    <div className="mb-3">
      <div className="f-s-12 f-w-600 text-secondary mb-2">
        <i className="ph-duotone ph-calendar-check me-1"></i>Daily Wallet Balance
      </div>
      <div className="table-responsive">
        <table className="table table-bordered mb-0" style={{ fontSize: 12 }}>
          <thead style={{ background: "#f8f9fc" }}>
            <tr>
              <th className="f-s-11 f-w-700 text-secondary">Date</th>
              <th className="f-s-11 f-w-700 text-secondary text-end">
                Opening Balance
              </th>
              <th className="f-s-11 f-w-700 text-secondary text-end">
                Closing Balance
              </th>
              <th className="f-s-11 f-w-700 text-secondary text-end">
                Net Change
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const net = parseFloat(
                (r.closing_balance - r.opening_balance).toFixed(2)
              );
              return (
                <tr key={i}>
                  <td>
                    <span className="badge text-light-primary f-s-11 px-2">
                      {r.date}
                    </span>
                  </td>
                  <td className="text-end f-w-500">{inr(r.opening_balance)}</td>
                  <td className="text-end f-w-600 text-primary">
                    {inr(r.closing_balance)}
                  </td>
                  <td className="text-end">
                    <span
                      className={`f-w-600 ${
                        net >= 0 ? "text-success" : "text-danger"
                      }`}
                    >
                      {net >= 0 ? "+" : ""}
                      {inr(net)}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
          {rows.length > 1 && (
            <tfoot
              style={{
                background: "#eef2ff",
                borderTop: "2px solid #c7d2fe"
              }}
            >
              <tr>
                <td className="f-s-11 f-w-700 text-primary">Period Total</td>
                <td className="text-end f-s-12 f-w-700 text-secondary">
                  {inr(rows[0].opening_balance)}
                </td>
                <td className="text-end f-s-12 f-w-700 text-primary">
                  {inr(rows[rows.length - 1].closing_balance)}
                </td>
                <td className="text-end f-s-12 f-w-700">
                  {(() => {
                    const net = parseFloat(
                      (
                        rows[rows.length - 1].closing_balance -
                        rows[0].opening_balance
                      ).toFixed(2)
                    );
                    return (
                      <span
                        className={net >= 0 ? "text-success" : "text-danger"}
                      >
                        {net >= 0 ? "+" : ""}
                        {inr(net)}
                      </span>
                    );
                  })()}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

// ─── wallet reconciliation card ───────────────────────────────────────────────
function ReconciliationCard({ rec, wallet }) {
  if (!rec) return null;
  const diff = rec.difference || 0;
  const diffColor =
    Math.abs(diff) < 0.1
      ? "text-success"
      : Math.abs(diff) < 5
      ? "text-warning"
      : "text-danger";
  return (
    <div className="mb-3">
      <div className="f-s-12 f-w-600 text-secondary mb-2">
        <i className="ph-duotone ph-scales me-1"></i>Wallet Reconciliation
      </div>
      <div className="card mb-0" style={{ borderLeft: "3px solid #7e57c2" }}>
        <div className="card-body py-2 px-3">
          {/* Formula header */}
          <div className="f-s-11 text-secondary mb-2">
            Opening + Fund In − Service Spend − Fund Out = Closing
          </div>
          <div className="row g-2">
            <div className="col-6 col-md-2">
              <div className="f-s-11 text-secondary">Opening</div>
              <div className="f-w-600 f-s-13">{inr(rec.opening_balance)}</div>
            </div>
            <div className="col-6 col-md-2">
              <div className="f-s-11 text-secondary">
                <i className="ph-fill ph-arrow-circle-down text-success me-1"></i>
                Fund In
              </div>
              <div className="f-w-600 f-s-13 text-success">
                +{inr(rec.fund_in)}
              </div>
            </div>
            <div className="col-6 col-md-2">
              <div className="f-s-11 text-secondary">
                <i className="ph-fill ph-arrow-circle-up text-warning me-1"></i>
                Service Spend
              </div>
              <div className="f-w-600 f-s-13 text-warning">
                −{inr(rec.service_spend)}
              </div>
            </div>
            <div className="col-6 col-md-2">
              <div className="f-s-11 text-secondary">
                <i className="ph-fill ph-arrow-circle-up text-danger me-1"></i>
                Fund Out
              </div>
              <div className="f-w-600 f-s-13 text-danger">
                −{inr(rec.fund_out)}
              </div>
            </div>
            <div className="col-6 col-md-2">
              <div className="f-s-11 text-secondary">Expected Closing</div>
              <div className="f-w-600 f-s-13 text-primary">
                {inr(rec.expected_closing)}
              </div>
            </div>
            <div className="col-6 col-md-2">
              <div className="f-s-11 text-secondary">Actual Closing</div>
              <div className="f-w-600 f-s-13 text-primary">
                {inr(rec.actual_closing)}
              </div>
            </div>
          </div>
          {/* Difference */}
          <div className="mt-2 pt-2" style={{ borderTop: "1px solid #e9ecef" }}>
            <span className="f-s-11 text-secondary me-2">Difference:</span>
            <span className={`f-w-700 f-s-13 ${diffColor}`}>
              {diff >= 0 ? "+" : ""}
              {inr(diff)}
            </span>
            {Math.abs(diff) < 0.1 && (
              <span className="badge text-light-success f-s-10 ms-2">
                <i className="ph-fill ph-check-circle me-1"></i>Balanced
              </span>
            )}
            {Math.abs(diff) >= 0.1 && (
              <span className="f-s-11 text-secondary ms-2">
                (commission credits, charges, holds, pending txns)
              </span>
            )}
          </div>
          {/* Fund movement breakdown if any */}
          {wallet && (wallet.fund_in > 0 || wallet.fund_out > 0) && (
            <div
              className="mt-2 pt-2 d-flex gap-4"
              style={{ borderTop: "1px solid #e9ecef" }}
            >
              <div>
                <span className="f-s-11 text-secondary">Total Fund Received: </span>
                <span className="f-w-600 text-success f-s-12">
                  {inr(wallet.fund_in)}
                </span>
              </div>
              <div>
                <span className="f-s-11 text-secondary">Total Fund Sent Out: </span>
                <span className="f-w-600 text-danger f-s-12">
                  {inr(wallet.fund_out)}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── expandable user row ──────────────────────────────────────────────────────
function UserRow({ user, isOpen, onToggle }) {
  const s = user.summary;
  const rec = user.reconciliation;
  const db = user.daily_balances || [];
  const inits = (user.user_name || "U")
    .trim()
    .split(" ")
    .map((p) => p[0] || "")
    .slice(0, 2)
    .join("")
    .toUpperCase();
  const activeSvcs = SVC_ORDER.filter((sl) => user.services?.[sl]);

  return (
    <>
      {/* ── Summary row ── */}
      <tr
        className="cursor-pointer border-bottom border-light-subtle"
        onClick={onToggle}
        style={{ cursor: "pointer" }}
      >
        <td>
          <div className="d-flex align-items-center gap-2">
            <span
              className="d-flex align-items-center justify-content-center rounded-circle fw-bold shadow-xs"
              style={{
                width: 36,
                height: 36,
                flexShrink: 0,
                backgroundColor: user.wl_name ? "#fff3cd" : "#f3e8ff",
                color: user.wl_name ? "#856404" : "#7e57c2",
                fontSize: "13px"
              }}
            >
              {inits}
            </span>
            <div>
              <div className="fw-semibold text-dark" style={{ fontSize: "13.5px" }}>
                {user.user_name || "—"}
                {user.wl_name && (
                  <span
                    className="badge rounded-pill bg-warning-subtle text-warning-emphasis ms-2"
                    style={{ fontSize: "10px" }}
                  >
                    <i className="ph-fill ph-buildings me-1"></i>WL
                  </span>
                )}
              </div>
              <div className="text-muted" style={{ fontSize: "12px" }}>
                {user.user_phone}
              </div>
              {user.wl_name && user.retailer_name !== user.user_name && (
                <div
                  className="f-s-11"
                  style={{ color: "var(--bs-warning-text,#664d03)" }}
                >
                  <i className="ph ph-user me-1"></i>Retailer: {user.retailer_name}
                </div>
              )}
            </div>
          </div>
        </td>
        <td>
          <span
            className="badge rounded-pill bg-light text-secondary border px-2.5 py-1 fw-semibold"
            style={{ fontSize: "11px" }}
          >
            {user.role}
          </span>
        </td>
        <td>
          <span
            className="badge rounded-pill bg-primary-subtle text-primary border border-primary-subtle px-2.5 py-1 fw-bold"
            style={{ fontSize: "12px" }}
          >
            {s.total_txn}
          </span>
        </td>
        <td>
          <span className="text-success fw-bold me-1" style={{ fontSize: "12.5px" }}>
            <i className="ph-fill ph-check-circle me-1"></i>
            {s.success_txn}
          </span>
          <div className="text-danger" style={{ fontSize: "11px" }}>
            {s.failed_txn} failed
          </div>
        </td>
        {/* Opening balance */}
        <td className="fw-medium text-secondary" style={{ fontSize: "13px" }}>
          {db.length > 0 ? inr(db[0].opening_balance) : "—"}
        </td>
        {/* Closing balance */}
        <td className="fw-bold text-primary" style={{ fontSize: "13px" }}>
          {db.length > 0 ? inr(db[db.length - 1].closing_balance) : "—"}
        </td>
        {/* Fund In */}
        <td className="fw-bold text-success" style={{ fontSize: "13px" }}>
          {rec ? inr(rec.fund_in) : "—"}
        </td>
        {/* Fund Out */}
        <td className="fw-bold text-danger" style={{ fontSize: "13px" }}>
          {rec ? inr(rec.fund_out) : "—"}
        </td>
        <td className="fw-semibold text-secondary" style={{ fontSize: "13px" }}>
          {inr(s.total_amount)}
        </td>
        <td className="fw-bold text-success" style={{ fontSize: "13px" }}>
          {inr(s.success_amount)}
        </td>
        <td className="fw-bold text-primary" style={{ fontSize: "13px" }}>
          {inr(s.total_commission)}
        </td>
        <td className="text-end pe-3">
          <div
            className="rounded-circle d-inline-flex align-items-center justify-content-center"
            style={{ width: "28px", height: "28px", backgroundColor: "#f1f5f9" }}
          >
            <i
              className={`ph-bold ${
                isOpen ? "ph-caret-up" : "ph-caret-down"
              } text-secondary`}
              style={{ fontSize: "12px" }}
            ></i>
          </div>
        </td>
      </tr>

      {/* ── Expanded detail ── */}
      {isOpen && (
        <tr>
          <td colSpan={12} style={{ padding: 0, background: "#f8fafc" }}>
            <div className="p-3.5">
              {/* Daily balance table */}
              <DailyBalanceTable rows={db} />

              {/* Reconciliation */}
              <ReconciliationCard rec={rec} wallet={user.wallet_summary} />

              {/* Service cards */}
              {activeSvcs.length > 0 && (
                <>
                  <div className="f-s-12 f-w-600 text-secondary mb-2">
                    <i className="ph-duotone ph-squares-four me-1"></i>Service Breakdown
                  </div>
                  <div className="row g-2">
                    {activeSvcs.map((slug) => {
                      const sv = user.services[slug];
                      const meta = SVC_META[slug] || {
                        label: slug,
                        icon: "ph-circle"
                      };
                      return (
                        <div className="col-md-6 col-xl-4" key={slug}>
                          <div
                            className="card mb-0 border-0 shadow-xs rounded-3 bg-white"
                            style={{ borderLeft: "4px solid #7e57c2" }}
                          >
                            <div className="card-body py-2.5 px-3">
                              <div className="d-flex align-items-center justify-content-between mb-2">
                                <div className="d-flex align-items-center gap-2">
                                  <i
                                    className={`ph-duotone ${meta.icon} text-primary`}
                                    style={{ fontSize: "18px" }}
                                  ></i>
                                  <span className="fw-semibold text-dark" style={{ fontSize: "13px" }}>
                                    {sv.label || meta.label}
                                  </span>
                                </div>
                                <span className="badge bg-primary-subtle text-primary rounded-pill px-2.5 py-1 fw-bold" style={{ fontSize: "11px" }}>
                                  {sv.total_txn} txn
                                </span>
                              </div>
                              <div className="d-flex gap-2 flex-wrap mb-2">
                                <span className="badge bg-success-subtle text-success rounded-pill px-2 py-0.5" style={{ fontSize: "11px" }}>
                                  <i className="ph-fill ph-check-circle me-1"></i>
                                  {sv.success_txn} success
                                </span>
                                <span className="badge bg-danger-subtle text-danger rounded-pill px-2 py-0.5" style={{ fontSize: "11px" }}>
                                  <i className="ph-fill ph-x-circle me-1"></i>
                                  {sv.failed_txn} failed
                                </span>
                                {sv.pending_txn > 0 && (
                                  <span className="badge bg-warning-subtle text-warning-emphasis rounded-pill px-2 py-0.5" style={{ fontSize: "11px" }}>
                                    <i className="ph-fill ph-clock me-1"></i>
                                    {sv.pending_txn} pending
                                  </span>
                                )}
                              </div>
                              <div className="d-flex justify-content-between">
                                <div>
                                  <div className="text-secondary f-s-11">Total amount</div>
                                  <div className="f-w-600 f-s-13">{inr(sv.total_amount)}</div>
                                </div>
                                <div className="text-end">
                                  <div className="text-secondary f-s-11">Success amt</div>
                                  <div className="f-w-600 f-s-13 text-success">
                                    {inr(sv.success_amount)}
                                  </div>
                                </div>
                                <div className="text-end">
                                  <div className="text-secondary f-s-11">Commission</div>
                                  <div className="f-w-600 f-s-13 text-primary">
                                    {inr(sv.commission)}
                                  </div>
                                </div>
                              </div>
                              {Object.keys(sv.operators || {}).length > 0 && (
                                <div
                                  className="mt-2 pt-2"
                                  style={{ borderTop: "1px solid #e9ecef" }}
                                >
                                  <div className="text-secondary f-s-11 f-w-500 mb-1">
                                    Operator wise
                                  </div>
                                  <OperatorList operators={sv.operators} />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ─── main component ───────────────────────────────────────────────────────────
function UserWiseServiceReportPage() {
  const { userid } = useSession();
  const { start, end } = getTodayRange();

  const [fromDate, setFromDate] = useState(start);
  const [toDate, setToDate] = useState(end);
  const [targetUser, setTargetUser] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedKey, setExpandedKey] = useState(null);
  const [activeTab, setActiveTab] = useState("users");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setExpandedKey(null);
    try {
      const url = buildUrl(userid, fromDate, toDate, targetUser);
      const res = await fetchWrapper(url);
      if (res.status) {
        setData(res);
        setPage(1);
      } else showMainToast(res.error || "Failed to load", "danger");
    } catch {
      showMainToast("Network error", "danger");
    } finally {
      setLoading(false);
    }
  }, [userid, fromDate, toDate, targetUser]);

  const g = data?.grand_total || {};
  const report = data?.report || [];

  const pageRows = useMemo(() => {
    const s = (page - 1) * PAGE_SIZE;
    return report.slice(s, s + PAGE_SIZE);
  }, [report, page]);

  // By Service cross-user summary
  const serviceSummary = useMemo(() => {
    if (!report.length) return [];
    return SVC_ORDER.map((slug) => {
      const meta = SVC_META[slug] || { label: slug, icon: "ph-circle" };
      let total_txn = 0,
        success_txn = 0,
        failed_txn = 0,
        pending_txn = 0;
      let total_amount = 0,
        success_amount = 0,
        commission = 0;
      const opMap = {};
      report.forEach((u) => {
        const sv = u.services?.[slug];
        if (!sv) return;
        total_txn += sv.total_txn || 0;
        success_txn += sv.success_txn || 0;
        failed_txn += sv.failed_txn || 0;
        pending_txn += sv.pending_txn || 0;
        total_amount += parseFloat(sv.total_amount) || 0;
        success_amount += parseFloat(sv.success_amount) || 0;
        commission += parseFloat(sv.commission) || 0;
        Object.entries(sv.operators || {}).forEach(([op, v]) => {
          if (!opMap[op]) opMap[op] = { txn: 0, amount: 0 };
          opMap[op].txn += v.txn || 0;
          opMap[op].amount += parseFloat(v.amount) || 0;
        });
      });
      if (total_txn === 0) return null;
      return {
        slug,
        ...meta,
        total_txn,
        success_txn,
        failed_txn,
        pending_txn,
        total_amount,
        success_amount,
        commission,
        operators: opMap
      };
    }).filter(Boolean);
  }, [report]);

  const exportCSV = () => {
    if (!report.length) {
      showMainToast("No data", "warning");
      return;
    }
    const rows = [
      [
        "BC Name",
        "Phone",
        "Retailer Name",
        "Role",
        "Opening Bal",
        "Closing Bal",
        "Fund In",
        "Fund Out",
        "Service",
        "Total Txn",
        "Success",
        "Failed",
        "Pending",
        "Total Amount",
        "Success Amt",
        "Commission"
      ].join(",")
    ];
    report.forEach((u) => {
      const db = u.daily_balances || [];
      const rec = u.reconciliation || {};
      const ob = db.length > 0 ? db[0].opening_balance : 0;
      const cb = db.length > 0 ? db[db.length - 1].closing_balance : 0;
      SVC_ORDER.forEach((slug) => {
        const sv = u.services?.[slug];
        if (!sv) return;
        rows.push(
          [
            u.user_name,
            u.user_phone,
            u.retailer_name,
            u.role,
            ob,
            cb,
            rec.fund_in || 0,
            rec.fund_out || 0,
            sv.label || slug,
            sv.total_txn,
            sv.success_txn,
            sv.failed_txn,
            sv.pending_txn,
            sv.total_amount,
            sv.success_amount,
            sv.commission
          ].join(",")
        );
      });
    });
    const a = document.createElement("a");
    a.href =
      "data:text/csv;charset=utf-8," + encodeURIComponent(rows.join("\n"));
    a.download = `user_wise_service_report_${fromDate}_to_${toDate}.csv`;
    a.click();
  };

  return (
    <main>
      <div className="container-fluid">
        {loading && (
          <div
            className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
            style={{ backgroundColor: "rgba(255, 255, 255, 0.75)", zIndex: 4000 }}
          >
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}

        {/* 1. Branded Hero Header Banner Card */}
        <div
          className="card border-0 mb-3 overflow-hidden position-relative shadow-sm"
          style={{
            background: "linear-gradient(135deg, #f5efff 0%, #e0e7ff 100%)",
            borderRadius: "14px"
          }}
        >
          <div className="card-body py-3 px-3.5 position-relative z-1">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
              <div className="d-flex align-items-center gap-3">
                <div
                  className="d-flex align-items-center justify-content-center flex-shrink-0 shadow-xs"
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "12px",
                    backgroundColor: "#7e57c2",
                    color: "#ffffff"
                  }}
                >
                  <i
                    className="ph-bold ph-chart-bar"
                    style={{ fontSize: "20px" }}
                  ></i>
                </div>
                <div>
                  <h5
                    className="fw-extrabold mb-0.5"
                    style={{ color: "#0f172a", fontSize: "17px" }}
                  >
                    User Wise Service Report
                  </h5>
                  <p
                    className="text-muted mb-1"
                    style={{ fontSize: "12px" }}
                  >
                    View detailed service usage by users with complete transaction insights.
                  </p>
                  <div className="d-flex align-items-center gap-1.5 text-secondary" style={{ fontSize: "11.5px" }}>
                    <i className="ph-bold ph-house-line" style={{ color: "#7e57c2" }}></i>
                    <span>Reports</span>
                    <span>/</span>
                    <span className="fw-semibold" style={{ color: "#7e57c2" }}>
                      User Wise Service
                    </span>
                  </div>
                </div>
              </div>

              <div className="d-flex align-items-center gap-3 flex-shrink-0">
                <button
                  type="button"
                  className="btn bg-white border border-light-subtle rounded-3 shadow-xs d-inline-flex align-items-center gap-1.5 px-3 py-1 fw-bold text-nowrap"
                  style={{
                    fontSize: "11.5px",
                    color: "#673ab7",
                    backgroundColor: "#ffffff",
                    height: "32px",
                    whiteSpace: "nowrap"
                  }}
                  onClick={exportCSV}
                >
                  <i
                    className="ph-bold ph-download-simple"
                    style={{ fontSize: "13px", color: "#673ab7" }}
                  ></i>
                  Export CSV
                </button>

                <img
                  src="/assets/images/user_wise_report_3d.png"
                  alt="User Wise Reports 3D Illustration"
                  className="d-none d-sm-block"
                  style={{
                    maxHeight: "70px",
                    objectFit: "contain"
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* 2. Standalone Filter Card */}
        <div className="card border-0 mb-3 rounded-4 shadow-sm bg-white p-4">
          {/* Header inside Filter Card */}
          <div className="d-flex align-items-center gap-3 mb-3">
            <div
              className="d-flex align-items-center justify-content-center flex-shrink-0 shadow-xs"
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "12px",
                backgroundColor: "#7e57c2",
                color: "#ffffff"
              }}
            >
              <i
                className="ph-bold ph-funnel"
                style={{ fontSize: "20px" }}
              ></i>
            </div>
            <div>
              <h5
                className="fw-bold text-dark mb-0.5"
                style={{ fontSize: "17px" }}
              >
                Filter
              </h5>
              <p className="text-muted mb-0" style={{ fontSize: "12.5px" }}>
                Select date range and retailer (optional) to generate report.
              </p>
            </div>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              fetchData();
            }}
          >
            <div className="row g-3 align-items-end">
              <div className="col-12 col-sm-6 col-md-3">
                <label
                  className="form-label text-secondary fw-semibold mb-1"
                  style={{ fontSize: "12px" }}
                >
                  From Date
                </label>
                <div className="position-relative">
                  <i
                    className="ph-bold ph-calendar-blank position-absolute top-50 start-0 translate-middle-y ms-3"
                    style={{ fontSize: "16px", color: "#7e57c2" }}
                  ></i>
                  <input
                    type="date"
                    className="form-control rounded-3 border-light-subtle shadow-xs ps-5 py-2"
                    style={{
                      fontSize: "13px",
                      backgroundColor: "#f8fafc",
                      color: "#1e293b",
                      height: "42px"
                    }}
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="col-12 col-sm-6 col-md-3">
                <label
                  className="form-label text-secondary fw-semibold mb-1"
                  style={{ fontSize: "12px" }}
                >
                  To Date
                </label>
                <div className="position-relative">
                  <i
                    className="ph-bold ph-calendar-blank position-absolute top-50 start-0 translate-middle-y ms-3"
                    style={{ fontSize: "16px", color: "#7e57c2" }}
                  ></i>
                  <input
                    type="date"
                    className="form-control rounded-3 border-light-subtle shadow-xs ps-5 py-2"
                    style={{
                      fontSize: "13px",
                      backgroundColor: "#f8fafc",
                      color: "#1e293b",
                      height: "42px"
                    }}
                    value={toDate}
                    min={fromDate}
                    onChange={(e) => setToDate(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="col-12 col-sm-6 col-md-3">
                <label
                  className="form-label text-secondary fw-semibold mb-1"
                  style={{ fontSize: "12px" }}
                >
                  Retailer ID <span className="text-muted fw-normal">(optional)</span>
                </label>
                <div className="position-relative">
                  <i
                    className="ph-bold ph-user position-absolute top-50 start-0 translate-middle-y ms-3"
                    style={{ fontSize: "16px", color: "#7e57c2" }}
                  ></i>
                  <input
                    type="text"
                    className="form-control rounded-3 border-light-subtle shadow-xs ps-5 py-2"
                    style={{
                      fontSize: "13px",
                      backgroundColor: "#f8fafc",
                      color: "#1e293b",
                      height: "42px"
                    }}
                    placeholder="Filter to one retailer"
                    value={targetUser}
                    onChange={(e) => setTargetUser(e.target.value)}
                  />
                </div>
              </div>

              <div className="col-12 col-sm-6 col-md-3">
                <button
                  type="submit"
                  className="btn text-white fw-bold d-inline-flex align-items-center justify-content-center gap-2 w-100 shadow-xs"
                  style={{
                    background:
                      "linear-gradient(135deg, #7e57c2 0%, #673ab7 100%)",
                    borderRadius: "12px",
                    height: "42px",
                    fontSize: "13.5px"
                  }}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1"></span>
                      Loading…
                    </>
                  ) : (
                    <>
                      <i
                        className="ph-bold ph-magnifying-glass"
                        style={{ fontSize: "16px" }}
                      ></i>
                      Fetch Report
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>

        {/* 3. Stat Cards Row */}
        {data && (
          <div className="row g-2.5 mb-3">
            <div className="col-6 col-md-4 col-xxl-2">
              <StatCard
                label="Total Users"
                value={data.total_users || 0}
                icon="ph-users-three"
                iconBg="#f3e8ff"
                iconColor="#7e57c2"
              />
            </div>
            <div className="col-6 col-md-4 col-xxl-2">
              <StatCard
                label="Total Txn"
                value={(g.total_txn || 0).toLocaleString("en-IN")}
                icon="ph-receipt"
                iconBg="#fef3c7"
                iconColor="#d97706"
              />
            </div>
            <div className="col-6 col-md-4 col-xxl-2">
              <StatCard
                label="Fund In (Total)"
                value={inr(g.total_fund_in)}
                icon="ph-arrow-circle-down"
                iconBg="#dcfce7"
                iconColor="#16a34a"
              />
            </div>
            <div className="col-6 col-md-4 col-xxl-2">
              <StatCard
                label="Fund Out (Total)"
                value={inr(g.total_fund_out)}
                icon="ph-arrow-circle-up"
                iconBg="#ffe4e6"
                iconColor="#f43f5e"
              />
            </div>
            <div className="col-6 col-md-4 col-xxl-2">
              <StatCard
                label="Service Amount"
                value={inr(g.success_amount)}
                icon="ph-check-circle"
                iconBg="#e0e7ff"
                iconColor="#4f46e5"
              />
            </div>
            <div className="col-6 col-md-4 col-xxl-2">
              <StatCard
                label="Commission"
                value={inr(g.total_commission)}
                icon="ph-coins"
                iconBg="#f1f5f9"
                iconColor="#475569"
              />
            </div>
          </div>
        )}

        {/* 4. Main Reports Container Card */}
        {data && (
          <div className="card border-0 rounded-4 shadow-sm overflow-hidden mb-4">
            {/* Header with View Tabs */}
            <div className="card-header bg-white p-3.5 border-bottom border-light-subtle d-flex align-items-center justify-content-between flex-wrap gap-3">
              <div className="bg-white p-1 rounded-pill shadow-xs border border-light-subtle d-inline-flex align-items-center">
                {[
                  { key: "users", label: "By User", icon: "ph-user-list" },
                  { key: "services", label: "By Service", icon: "ph-squares-four" }
                ].map(({ key, label, icon }, idx) => (
                  <React.Fragment key={key}>
                    {idx > 0 &&
                      activeTab !== key &&
                      activeTab !== (idx === 1 ? "users" : "services") && (
                        <span
                          className="text-light-subtle px-1"
                          style={{ fontSize: "14px" }}
                        >
                          |
                        </span>
                      )}
                    <button
                      type="button"
                      className="btn btn-sm rounded-pill px-3.5 py-1.5 fw-semibold border-0 text-nowrap d-inline-flex align-items-center gap-1.5"
                      style={{
                        fontSize: "12.5px",
                        whiteSpace: "nowrap",
                        backgroundColor:
                          activeTab === key ? "#7e57c2" : "transparent",
                        color: activeTab === key ? "#ffffff" : "#64748b",
                        transition: "all 0.15s ease"
                      }}
                      onClick={() => setActiveTab(key)}
                    >
                      <i className={`ph-bold ${icon}`}></i>
                      {label}
                    </button>
                  </React.Fragment>
                ))}
              </div>

              <span className="text-muted fw-medium" style={{ fontSize: "12.5px" }}>
                {data.from} — {data.to} · Total {data.total_users} user
                {data.total_users !== 1 ? "s" : ""}
              </span>
            </div>

            <div className="card-body p-0">
              {/* ── By User tab ── */}
              {activeTab === "users" && (
                <>
                  <div className="table-responsive app-scroll">
                    {report.length === 0 ? (
                      <div className="text-center py-5 px-3">
                        <div className="mb-3 d-inline-flex align-items-center justify-content-center">
                          <div
                            className="d-flex align-items-center justify-content-center rounded-circle"
                            style={{
                              width: "72px",
                              height: "72px",
                              backgroundColor: "#f3e8ff",
                              color: "#7e57c2"
                            }}
                          >
                            <i
                              className="ph-duotone ph-users"
                              style={{ fontSize: "36px" }}
                            ></i>
                          </div>
                        </div>
                        <h6
                          className="fw-bold text-dark mb-1"
                          style={{ fontSize: "16px" }}
                        >
                          No transactions found
                        </h6>
                        <p
                          className="text-muted mb-0"
                          style={{ fontSize: "13px" }}
                        >
                          No user activity recorded for this period
                        </p>
                      </div>
                    ) : (
                      <table className="table align-middle mb-0 text-nowrap">
                        <thead
                          style={{
                            backgroundColor: "#f5efff",
                            color: "#334155"
                          }}
                        >
                          <tr>
                            <th style={{ fontSize: "12.5px", fontWeight: "700", padding: "12px 14px" }}>
                              User
                            </th>
                            <th style={{ fontSize: "12.5px", fontWeight: "700", padding: "12px 14px" }}>
                              Role
                            </th>
                            <th style={{ fontSize: "12.5px", fontWeight: "700", padding: "12px 14px" }}>
                              Total Txn
                            </th>
                            <th style={{ fontSize: "12.5px", fontWeight: "700", padding: "12px 14px" }}>
                              Success / Failed
                            </th>
                            <th style={{ fontSize: "12.5px", fontWeight: "700", padding: "12px 14px" }}>
                              Opening Bal
                            </th>
                            <th style={{ fontSize: "12.5px", fontWeight: "700", padding: "12px 14px" }}>
                              Closing Bal
                            </th>
                            <th style={{ fontSize: "12.5px", fontWeight: "700", padding: "12px 14px" }}>
                              Fund In
                            </th>
                            <th style={{ fontSize: "12.5px", fontWeight: "700", padding: "12px 14px" }}>
                              Fund Out
                            </th>
                            <th style={{ fontSize: "12.5px", fontWeight: "700", padding: "12px 14px" }}>
                              Svc Amount
                            </th>
                            <th style={{ fontSize: "12.5px", fontWeight: "700", padding: "12px 14px" }}>
                              Success Amt
                            </th>
                            <th style={{ fontSize: "12.5px", fontWeight: "700", padding: "12px 14px" }}>
                              Commission
                            </th>
                            <th style={{ width: "40px" }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {pageRows.map((user) => (
                            <UserRow
                              key={user.user_id}
                              user={user}
                              isOpen={expandedKey === user.user_id}
                              onToggle={() =>
                                setExpandedKey(
                                  expandedKey === user.user_id
                                    ? null
                                    : user.user_id
                                )
                              }
                            />
                          ))}
                        </tbody>
                        <tfoot
                          style={{
                            backgroundColor: "#f8fafc",
                            borderTop: "2px solid #e2e8f0"
                          }}
                        >
                          <tr>
                            <td
                              colSpan={2}
                              className="fw-bold text-dark"
                              style={{ fontSize: "13px", padding: "12px 14px" }}
                            >
                              TOTAL ({report.length} users)
                            </td>
                            <td
                              className="fw-bold text-dark"
                              style={{ fontSize: "13px", padding: "12px 14px" }}
                            >
                              {(g.total_txn || 0).toLocaleString("en-IN")}
                            </td>
                            <td
                              className="fw-bold text-success"
                              style={{ fontSize: "13px", padding: "12px 14px" }}
                            >
                              {(g.success_txn || 0).toLocaleString("en-IN")} /{" "}
                              <span className="text-danger">
                                {(g.failed_txn || 0).toLocaleString("en-IN")}
                              </span>
                            </td>
                            <td className="text-muted" style={{ padding: "12px 14px" }}>
                              —
                            </td>
                            <td className="text-muted" style={{ padding: "12px 14px" }}>
                              —
                            </td>
                            <td
                              className="fw-bold text-success"
                              style={{ fontSize: "13px", padding: "12px 14px" }}
                            >
                              {inr(g.total_fund_in)}
                            </td>
                            <td
                              className="fw-bold text-danger"
                              style={{ fontSize: "13px", padding: "12px 14px" }}
                            >
                              {inr(g.total_fund_out)}
                            </td>
                            <td
                              className="fw-bold text-dark"
                              style={{ fontSize: "13px", padding: "12px 14px" }}
                            >
                              {inr(g.total_amount)}
                            </td>
                            <td
                              className="fw-bold text-success"
                              style={{ fontSize: "13px", padding: "12px 14px" }}
                            >
                              {inr(g.success_amount)}
                            </td>
                            <td
                              className="fw-bold text-primary"
                              style={{ fontSize: "13px", padding: "12px 14px" }}
                            >
                              {inr(g.total_commission)}
                            </td>
                            <td></td>
                          </tr>
                        </tfoot>
                      </table>
                    )}
                  </div>

                  {/* Pagination */}
                  {report.length > PAGE_SIZE && (
                    <div className="card-footer bg-white p-3 border-top border-light-subtle d-flex flex-wrap align-items-center justify-content-between gap-3">
                      <span className="text-muted" style={{ fontSize: "13px" }}>
                        Showing {(page - 1) * PAGE_SIZE + 1} to{" "}
                        {Math.min(page * PAGE_SIZE, report.length)} of{" "}
                        {report.length} entries
                      </span>

                      <div className="d-flex align-items-center gap-2">
                        <button
                          type="button"
                          className="btn btn-sm rounded-pill px-3 py-1.5 border border-light-subtle fw-semibold d-inline-flex align-items-center gap-1"
                          style={{
                            fontSize: "12.5px",
                            backgroundColor: page === 1 ? "#f8fafc" : "#f1f5f9",
                            color: page === 1 ? "#94a3b8" : "#7e57c2"
                          }}
                          disabled={page === 1}
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                        >
                          <i
                            className="ph-bold ph-caret-left"
                            style={{ fontSize: "12px" }}
                          ></i>
                          Previous
                        </button>

                        <button
                          type="button"
                          className="btn btn-sm rounded-pill px-3 py-1.5 border border-light-subtle fw-semibold d-inline-flex align-items-center gap-1"
                          style={{
                            fontSize: "12.5px",
                            backgroundColor:
                              page * PAGE_SIZE >= report.length
                                ? "#f8fafc"
                                : "#f1f5f9",
                            color:
                              page * PAGE_SIZE >= report.length
                                ? "#94a3b8"
                                : "#7e57c2"
                          }}
                          disabled={page * PAGE_SIZE >= report.length}
                          onClick={() => setPage((p) => p + 1)}
                        >
                          Next
                          <i
                            className="ph-bold ph-caret-right"
                            style={{ fontSize: "12px" }}
                          ></i>
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* ── By Service tab ── */}
              {activeTab === "services" && (
                <div className="table-responsive app-scroll">
                  {serviceSummary.length === 0 ? (
                    <div className="text-center py-5 px-3">
                      <i
                        className="ph-duotone ph-squares-four text-muted d-block mb-2"
                        style={{ fontSize: "42px" }}
                      ></i>
                      <h6 className="fw-bold text-dark mb-0">No service data</h6>
                    </div>
                  ) : (
                    <table className="table align-middle mb-0 text-nowrap">
                      <thead
                        style={{ backgroundColor: "#f5efff", color: "#334155" }}
                      >
                        <tr>
                          <th style={{ fontSize: "12.5px", fontWeight: "700", padding: "12px 14px" }}>
                            Service
                          </th>
                          <th style={{ fontSize: "12.5px", fontWeight: "700", padding: "12px 14px" }}>
                            Total Txn
                          </th>
                          <th style={{ fontSize: "12.5px", fontWeight: "700", padding: "12px 14px" }}>
                            Success
                          </th>
                          <th style={{ fontSize: "12.5px", fontWeight: "700", padding: "12px 14px" }}>
                            Failed
                          </th>
                          <th style={{ fontSize: "12.5px", fontWeight: "700", padding: "12px 14px" }}>
                            Pending
                          </th>
                          <th style={{ fontSize: "12.5px", fontWeight: "700", padding: "12px 14px" }}>
                            Total Amount
                          </th>
                          <th style={{ fontSize: "12.5px", fontWeight: "700", padding: "12px 14px" }}>
                            Success Amt
                          </th>
                          <th style={{ fontSize: "12.5px", fontWeight: "700", padding: "12px 14px" }}>
                            Commission
                          </th>
                          <th style={{ fontSize: "12.5px", fontWeight: "700", padding: "12px 14px" }}>
                            Top Operators
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {serviceSummary.map((sv) => (
                          <tr
                            key={sv.slug}
                            className="border-bottom border-light-subtle"
                          >
                            <td style={{ padding: "12px 14px" }}>
                              <div className="d-flex align-items-center gap-2">
                                <i
                                  className={`ph-duotone ${sv.icon} text-primary`}
                                  style={{ fontSize: "18px" }}
                                ></i>
                                <span
                                  className="fw-semibold text-dark"
                                  style={{ fontSize: "13px" }}
                                >
                                  {sv.label}
                                </span>
                              </div>
                            </td>
                            <td style={{ padding: "12px 14px" }}>
                              <span
                                className="badge rounded-pill bg-primary-subtle text-primary border border-primary-subtle px-2.5 py-1 fw-bold"
                                style={{ fontSize: "12px" }}
                              >
                                {sv.total_txn.toLocaleString("en-IN")}
                              </span>
                            </td>
                            <td
                              className="text-success fw-bold"
                              style={{ fontSize: "13px", padding: "12px 14px" }}
                            >
                              {sv.success_txn.toLocaleString("en-IN")}
                            </td>
                            <td
                              className="text-danger fw-semibold"
                              style={{ fontSize: "13px", padding: "12px 14px" }}
                            >
                              {sv.failed_txn.toLocaleString("en-IN")}
                            </td>
                            <td
                              className="text-warning fw-semibold"
                              style={{ fontSize: "13px", padding: "12px 14px" }}
                            >
                              {sv.pending_txn.toLocaleString("en-IN")}
                            </td>
                            <td
                              className="fw-semibold text-secondary"
                              style={{ fontSize: "13px", padding: "12px 14px" }}
                            >
                              {inr(sv.total_amount)}
                            </td>
                            <td
                              className="fw-bold text-success"
                              style={{ fontSize: "13px", padding: "12px 14px" }}
                            >
                              {inr(sv.success_amount)}
                            </td>
                            <td
                              className="fw-bold text-primary"
                              style={{ fontSize: "13px", padding: "12px 14px" }}
                            >
                              {inr(sv.commission)}
                            </td>
                            <td style={{ minWidth: 180, padding: "12px 14px" }}>
                              {Object.entries(sv.operators || {})
                                .sort((a, b) => b[1].amount - a[1].amount)
                                .slice(0, 3)
                                .map(([op, v], i) => (
                                  <div
                                    key={i}
                                    className="d-flex justify-content-between gap-3"
                                  >
                                    <span
                                      className="text-secondary"
                                      style={{ fontSize: "11px" }}
                                    >
                                      {op}
                                    </span>
                                    <span
                                      className="fw-semibold text-primary"
                                      style={{ fontSize: "11px" }}
                                    >
                                      {v.txn} · {inr(v.amount)}
                                    </span>
                                  </div>
                                ))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot
                        style={{
                          backgroundColor: "#f8fafc",
                          borderTop: "2px solid #e2e8f0"
                        }}
                      >
                        <tr>
                          <td
                            className="fw-bold text-dark"
                            style={{ fontSize: "13px", padding: "12px 14px" }}
                          >
                            TOTAL ({serviceSummary.length} services)
                          </td>
                          <td
                            className="fw-bold text-dark"
                            style={{ fontSize: "13px", padding: "12px 14px" }}
                          >
                            {(g.total_txn || 0).toLocaleString("en-IN")}
                          </td>
                          <td
                            className="fw-bold text-success"
                            style={{ fontSize: "13px", padding: "12px 14px" }}
                          >
                            {(g.success_txn || 0).toLocaleString("en-IN")}
                          </td>
                          <td
                            className="fw-bold text-danger"
                            style={{ fontSize: "13px", padding: "12px 14px" }}
                          >
                            {(g.failed_txn || 0).toLocaleString("en-IN")}
                          </td>
                          <td
                            className="fw-bold text-warning"
                            style={{ fontSize: "13px", padding: "12px 14px" }}
                          >
                            {(g.pending_txn || 0).toLocaleString("en-IN")}
                          </td>
                          <td
                            className="fw-bold text-dark"
                            style={{ fontSize: "13px", padding: "12px 14px" }}
                          >
                            {inr(g.total_amount)}
                          </td>
                          <td
                            className="fw-bold text-success"
                            style={{ fontSize: "13px", padding: "12px 14px" }}
                          >
                            {inr(g.success_amount)}
                          </td>
                          <td
                            className="fw-bold text-primary"
                            style={{ fontSize: "13px", padding: "12px 14px" }}
                          >
                            {inr(g.total_commission)}
                          </td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Empty state prompt before fetching */}
        {!data && !loading && (
          <div className="card border-0 rounded-4 shadow-sm bg-white p-5 text-center my-4">
            <div className="mb-3 d-inline-flex align-items-center justify-content-center position-relative">
              <div
                className="d-flex align-items-center justify-content-center rounded-circle"
                style={{
                  width: "80px",
                  height: "80px",
                  backgroundColor: "#f3e8ff",
                  color: "#7e57c2"
                }}
              >
                <i
                  className="ph-duotone ph-user-list"
                  style={{ fontSize: "40px" }}
                ></i>
              </div>
            </div>
            <h5 className="fw-bold text-dark mb-1" style={{ fontSize: "18px" }}>
              Select a date range and click Fetch Report
            </h5>
            <p
              className="text-muted mx-auto mb-4"
              style={{ fontSize: "13.5px", maxWidth: "560px" }}
            >
              Analyze every service with operator breakdowns, daily opening & closing balance tracking, fund transfers in/out, and full wallet reconciliation per retailer.
            </p>
            <div className="d-flex justify-content-center w-100">
              <button
                type="button"
                className="btn text-white fw-bold px-3.5 py-1.5 rounded-pill shadow-xs d-inline-flex align-items-center justify-content-center gap-2"
                style={{
                  background:
                    "linear-gradient(135deg, #7e57c2 0%, #673ab7 100%)",
                  fontSize: "12.5px",
                  height: "36px",
                  minWidth: "160px",
                  maxWidth: "200px"
                }}
                onClick={fetchData}
              >
                <i
                  className="ph-bold ph-paper-plane-tilt"
                  style={{ fontSize: "14px" }}
                ></i>
                Fetch Report Now
              </button>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}

export default UserWiseServiceReportPage;